import { Box, Typography, LinearProgress, Avatar } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Lesson } from "./ArticleReader";

interface LessonSidebarProps {
  lessons: Lesson[];
  currentLessonIndex: number;
  totalLessons: number;
  progress: number;
  onSelectLesson: (index: number) => void;
}

export default function LessonSidebar({
  lessons,
  currentLessonIndex,
  totalLessons,
  progress,
  onSelectLesson,
}: LessonSidebarProps) {
  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: 3,
        p: 3,
        height: "100%",
        position: { lg: "sticky" },
        top: { lg: 20 },
      }}
    >
      {/* Progress Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#4B5563" }}
          >
            Lesson {currentLessonIndex + 1} of {totalLessons}
          </Typography>
          <Typography
            sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#4B5563" }}
          >
            {progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "#f0f0f0",
            "& .MuiLinearProgress-bar": {
              bgcolor: "#724E41",
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Lessons List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {lessons.map((lesson, index) => {
          const isActive = index === currentLessonIndex;
          const isCompleted = index < currentLessonIndex;

          return (
            <Box
              key={lesson.id}
              onClick={() => onSelectLesson(index)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: isActive ? "#F7E6D8" : "transparent",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: isActive
                  ? "1px solid #724E41"
                  : "1px solid transparent",
                "&:hover": {
                  bgcolor: isActive ? "#f0e6dc" : "#f9f9f9",
                },
              }}
            >
              {/* Lesson Number or Check Icon */}
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: isActive
                    ? "#C9B1A1"
                    : isCompleted
                    ? "#4caf50"
                    : "#e0e0e0",
                  color: isActive || isCompleted ? "#724E41" : "#666",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                }}
              >
                {isCompleted ? <CheckCircleIcon /> : index + 1}
              </Avatar>

              {/* Lesson Info */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#333" : "#666",
                    lineHeight: 1.4,
                  }}
                >
                  {lesson.title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.85rem", color: "#999", mt: 0.5 }}
                >
                  {lesson.duration}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
